import type { AxiosResponse } from 'axios';

import type { PageMessage } from '../../../../types/PageMessage';
import type { DemandCalculation } from '../../../demand-definition/types/DemandCalculation';
import type { DemandDataBasis } from '../../../demand-definition/types/DemandDataBasis';
import type { DemandDefinition } from '../../../demand-definition/types/DemandDefinition';
import type { DemandDefinitionInput } from '../../../demand-definition/types/DemandDefinitionInput';
import type { DemandDefinitionSummary } from '../../../demand-definition/types/DemandDefinitionSummary';
import type { DemandFleetCompany } from '../../../demand-definition/types/DemandFleetCompany';
import { authorizedAxiosInstance } from '../instances/AuthorizedAxiosInstance';

export interface GetDemandDefinitionsParams {
    page: number;
    customerGroupId?: string;
}

export const getDemandDefinitions = async (
    params: GetDemandDefinitionsParams
): Promise<PageMessage<DemandDefinitionSummary>> => {
    const response = await authorizedAxiosInstance.get<PageMessage<DemandDefinitionSummary>>('/v1/demand-definitions', {
        params,
    });

    return response.data;
};

export const getDemandDefinition = async (id: string): Promise<DemandDefinition> => {
    const response = await authorizedAxiosInstance.get<DemandDefinition>(`/v1/demand-definitions/${id}`);
    return response.data;
};

export interface GetDemandDefinitionFleetParams {
    customerGroupId?: string;
    definitionId?: string;
}

export const getDemandDefinitionFleet = async (
    params: GetDemandDefinitionFleetParams
): Promise<DemandFleetCompany[]> => {
    const response = await authorizedAxiosInstance.get<DemandFleetCompany[]>('/v1/demand-definitions/fleet', {
        params,
    });
    return response.data;
};

export const calculateDemand = async (
    input: DemandDefinitionInput,
    customerGroupId?: string,
    definitionId?: string
): Promise<DemandCalculation> => {
    const response = await authorizedAxiosInstance.post<DemandCalculation>('/v1/demand-definitions/calculate', input, {
        params: { customerGroupId, definitionId },
    });
    return response.data;
};

export const exportDemandCalculation = (
    input: DemandDefinitionInput,
    customerGroupId?: string,
    definitionId?: string
): Promise<AxiosResponse<Blob>> =>
    authorizedAxiosInstance.post('/v1/demand-definitions/calculate/export', input, {
        params: { customerGroupId, definitionId },
        responseType: 'blob',
    });

export interface SaveDemandDefinitionRequest {
    name: string;
    reference?: string;
    internalNotes?: string;
    input: DemandDefinitionInput;
}

export const createDemandDefinition = async (
    request: SaveDemandDefinitionRequest,
    customerGroupId?: string
): Promise<DemandDefinition> => {
    const response = await authorizedAxiosInstance.post<DemandDefinition>('/v1/demand-definitions', request, {
        params: { customerGroupId },
    });
    return response.data;
};

interface UpdateDemandDefinitionRequest {
    id: string;
    request: SaveDemandDefinitionRequest;
    customerGroupId?: string;
}

export const updateDemandDefinition = async ({
    id,
    request,
    customerGroupId,
}: UpdateDemandDefinitionRequest): Promise<DemandDefinition> => {
    const response = await authorizedAxiosInstance.put<DemandDefinition>(`/v1/demand-definitions/${id}`, request, {
        params: { customerGroupId },
    });
    return response.data;
};

export interface PatchDemandDefinitionRequest {
    dataBasis: DemandDataBasis;
}

interface PatchDemandDefinitionParams {
    id: string;
    request: PatchDemandDefinitionRequest;
    customerGroupId?: string;
}

export const patchDemandDefinition = async ({
    id,
    request,
    customerGroupId,
}: PatchDemandDefinitionParams): Promise<DemandDefinition> => {
    const response = await authorizedAxiosInstance.patch<DemandDefinition>(`/v1/demand-definitions/${id}`, request, {
        params: { customerGroupId },
    });
    return response.data;
};
